interface DebouncePromiseMap {
    [key: string]: Promise<any>;
}

export class BaseProvider {
    private loaded = false;
    private dependenciesLoaded = false;

    private loadedPromise: Promise<any> | null;
    private dependenciesLoadedPromise: Promise<any> | null;

    private debouncePromises: DebouncePromiseMap = {};

    protected async ensureDependenciesLoaded(providers): Promise<void> {
        try {
            if (!this.dependenciesLoaded) {
                if (this.dependenciesLoadedPromise) {
                    return this.dependenciesLoadedPromise;
                }

                this.dependenciesLoadedPromise = new Promise<void>(async (resolve, reject) => {
                    try {
                        const promises = providers.map(provider => {
                            return provider.ensureLoaded(() => provider.load());
                        });

                        await Promise.all(promises);

                        resolve();
                    } catch (err) {
                        reject(err);
                    }
                });

                await this.dependenciesLoadedPromise;

                this.dependenciesLoaded = true;
            }
        } catch (err) {
            throw err;
        } finally {
            this.dependenciesLoadedPromise = null;
        }
    }

    public async ensureLoaded(callback, force: boolean = false): Promise<any> {
        try {
            if (this.loaded && !force) {
                return Promise.resolve();
            }

            if (this.loadedPromise) {
                return this.loadedPromise;
            }

            // Create a wrapping promise around the callback
            this.loadedPromise = new Promise(async (resolve, reject) => {
                try {
                    const result = await callback();
                    return resolve(result);
                } catch (err) {
                    reject(err);
                }
            });

            const result = await this.loadedPromise;
            this.loaded = true;

            return result;
        } catch (err) {
            throw err;
        } finally {
            this.loadedPromise = null;
        }
    }

    public isLoaded(): boolean {
        return this.loaded;
    }

    protected resetInfo(): void {
        this.loaded = false;
        this.dependenciesLoaded = false;
    }

    /**
     * Provide a central location to fetch a dataset
     * This will prevent us from making duplicate reload calls when a boost:finished event happens or something similar
     */
    protected async debounce(key: string, callback: () => Promise<any>, timeout = 0): Promise<any> {
        if (this.debouncePromises[key] !== undefined) {
            return this.debouncePromises[key];
        }

        try {
            this.debouncePromises[key] = callback();
            return await this.debouncePromises[key];
        } finally {
            setTimeout(() => {
                delete this.debouncePromises[key];
            }, timeout);
        }
    }
}



// WEBPACK FOOTER //
// ./src/providers/base.ts